import { Box, Badge } from "@mui/material";
import MDTypography from "components/MDTypography";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import DescriptionIcon from "@mui/icons-material/Description";
import moment from "moment";

export default function AgendaEventCard({
    evento,
    darkMode,
    tipoEventoEnum,
    statusPrazoEnum,
    sistemasEnum,
    tipoPartesEnum,
    onClick,
    showDate = false,
}) {
    const isSuspension = evento.tipoEvento === "endSuspension";
    const isFichaAgendada = evento.tipoEventoStr === "ficha_agendada";

    const statusCode = evento.prazo?.statusPrazo || evento.statusPrazo || evento.status;
    const isFinished = statusCode === "finished" || statusCode === "completed" || statusCode === "concluido";

    const textColor = (darkMode && isFinished) ? "#000000" : (darkMode ? "#ffffff" : "#000000");


    /* ===================== FICHA AGENDADA ===================== */
    if (isFichaAgendada) {
        let timeStr = moment(evento.data).format("HH:mm");
        if (timeStr === "00:00") timeStr = "";
        
        const isAtrasada = moment(evento.data).isBefore(moment(), "day");
        
        return (
            <Box
                onClick={(e) => {
                    e.stopPropagation();
                    // Extract ficha ID from "FICHA_123"
                    const fichaId = evento.id.replace("FICHA_", "");
                    // Navigate to Ficha (handled by parent through a different route, or we trigger an open event)
                    // For now, trigger onClick which parent handles. Parent Home.js should handle it if event.tipoEventoStr is set.
                    onClick(evento);
                }}
                sx={{ 
                    display: "flex", 
                    alignItems: "center", 
                    justifyContent: "space-between", 
                    backgroundColor: isAtrasada ? (darkMode ? "rgba(244, 67, 53, 0.2)" : "rgba(244, 67, 53, 0.1)") : "transparent", 
                    p: 0.3, 
                    px: 0.5, 
                    mb: 0.2, 
                    cursor: "pointer", 
                    borderRadius: 1,
                    borderLeft: isAtrasada ? "3px solid #F44335" : "3px solid #FB8C00",
                    "&:hover": {
                        backgroundColor: darkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"
                    }
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", overflow: "hidden", flex: 1 }}>
                    <MDTypography 
                        variant="caption" 
                        sx={{ 
                            fontWeight: "bold", 
                            color: isAtrasada ? "#F44335" : (darkMode ? "#FB8C00" : "#E65100"), 
                            fontSize: "0.65rem",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis"
                        }}
                    >
                        {evento.prazo?.descricao || "Ficha Agendada"}
                    </MDTypography>
                </Box>
                {timeStr && (
                    <MDTypography 
                        variant="caption" 
                        sx={{ 
                            color: isAtrasada ? "#F44335" : (darkMode ? "#FB8C00" : "#E65100"),
                            fontSize: "0.6rem", 
                            fontWeight: "bold",
                            ml: 1,
                            flexShrink: 0 
                        }}
                    >
                        {timeStr}
                    </MDTypography>
                )}
            </Box>
        );
    }


    /* ===================== SUSPENSÃO ===================== */
    if (isSuspension) {
        const p = evento.pessoaProcesso || evento.processoParte || {};
        const nomeParte = p.nome ? p.nome.split(' ')[0] : (p.tbPessoa?.nome ? p.tbPessoa?.nome.split(' ')[0] : '');

        return (
            <Box
                onClick={() => onClick(evento)}
                sx={{ 
                    display: "flex", 
                    alignItems: "center", 
                    justifyContent: "space-between", 
                    backgroundColor: "transparent", 
                    p: 0.3, 
                    px: 0.5, 
                    mb: 0.2, 
                    cursor: "pointer", 
                    borderRadius: 1,
                    "&:hover": {
                        backgroundColor: darkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"
                    }
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", overflow: "hidden", flex: 1 }}>
                    <MDTypography 
                        variant="caption" 
                        sx={{ 
                            fontWeight: "normal", 
                            color: textColor, 
                            fontSize: "0.65rem",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis"
                        }}
                    >
                        SUSPENSO{nomeParte ? ` - ${nomeParte}` : ''}
                    </MDTypography>
                </Box>
            </Box>
        );
    }

    /* ===================== PRAZO / AGENDA NORMAL ===================== */

    const tipoPrazoCode = evento.prazo?.tipoPrazo || evento.tipoEvento;
    const tipoPrazoName = tipoEventoEnum.find(t => t.code === tipoPrazoCode)?.name;


    const isUrgent = tipoPrazoName?.toLowerCase().includes("peremptório");

    let timeStr = moment(evento.data).format("HH:mm");
    if (timeStr === "00:00") timeStr = "";

    const titlePart = tipoPrazoName || 'Evento';
    const pInfo = evento.prazo?.processo || evento.processo || {};
    const descText = pInfo.cliente || pInfo.processo || evento.descricao || evento.prazo?.descricao || "";

    const fullText = descText ? `${titlePart}: ${descText}` : titlePart;

    return (
        <Box
            onClick={() => onClick(evento)}
            sx={{ 
                display: "flex", 
                alignItems: "center", 
                justifyContent: "space-between", 
                backgroundColor: "transparent", 
                p: 0.3, 
                px: 0.5, 
                mb: 0.2, 
                cursor: "pointer", 
                borderRadius: 1,
                "&:hover": {
                    backgroundColor: darkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"
                }
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", overflow: "hidden", flex: 1 }}>
                <MDTypography 
                    variant="caption" 
                    sx={{ 
                        color: isUrgent ? "#ff1b1b" : textColor,
                        fontSize: "0.65rem",
                        fontWeight: isUrgent ? "bold" : "normal",
                        textDecoration: isFinished ? "line-through" : "none",
                        opacity: isFinished ? 0.6 : 1,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis"
                    }}
                >
                    {fullText}
                </MDTypography>
            </Box>

            {timeStr && (
                <MDTypography 
                    variant="caption" 
                    sx={{ 
                        color: darkMode ? "#aaa" : "#666", 
                        fontSize: "0.6rem", 
                        textDecoration: isFinished ? "line-through" : "none",
                        opacity: isFinished ? 0.6 : 1,
                        ml: 1,
                        flexShrink: 0 
                    }}
                >
                    {timeStr}
                </MDTypography>
            )}
        </Box>
    );
}
